import { useMemo, useState } from "react";
import { useAppearance } from "../appearanceContext";
import { SERIES_DARK, SERIES_LIGHT } from "../chartMath";
import { rupee } from "../format";
import type { OpenPosition } from "../types";

/**
 * Where the money actually sits — by stock, by sector, by industry.
 *
 * Valued at market where the position could be priced and at cost where it
 * could not, because a holding with no quote is still money committed and
 * dropping it would understate every share.
 *
 * Eight slices plus a grey "Others". That cap is not cosmetic: past eight, a
 * ninth hue is indistinguishable from one already on screen for a colourblind
 * reader. Every slice is also labelled and a table view is one click away, so
 * nothing here is identified by colour alone.
 */

const OTHERS_LIGHT = "#b8c0cc";
const OTHERS_DARK = "#5a6475";
const MAX_SLICES = 8;

type View = "donut" | "bar" | "table";

interface Slice {
  key: string;
  value: number;
  pct: number;
  colour: string;
}

function group(
  rows: OpenPosition[],
  of: (p: OpenPosition) => string,
  series: readonly string[],
  others: string,
): Slice[] {
  const totals = new Map<string, number>();
  for (const p of rows) {
    const value = p.position_value ?? p.cost_basis;
    totals.set(of(p), (totals.get(of(p)) ?? 0) + value);
  }

  const sorted = [...totals.entries()]
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);

  const head = sorted.slice(0, MAX_SLICES);
  const tail = sorted.slice(MAX_SLICES);
  if (tail.length > 0) {
    head.push({ key: "Others", value: tail.reduce((a, t) => a + t.value, 0) });
  }

  const total = sorted.reduce((a, t) => a + t.value, 0) || 1;
  return head.map((slice, i) => ({
    ...slice,
    pct: (slice.value / total) * 100,
    // "Others" is not a category, so it never takes a category's colour.
    colour: slice.key === "Others" ? others : series[i % series.length],
  }));
}

/** NSE classifies four levels deep. Macro is deliberately not offered — it is
 *  too coarse to act on, and Sector already says what the business is. */
const LEVELS = {
  industry: { label: "By Industry", unit: "industries" },
  basic_industry: { label: "By Basic Industry", unit: "basic industries" },
} as const;

type Level = keyof typeof LEVELS;

export function Breakdown({ positions }: { positions: OpenPosition[] }) {
  // The third panel shows one level at a time rather than a fourth donut:
  // four of them leaves each too narrow to read.
  const [level, setLevel] = useState<Level>("industry");
  const { appearance } = useAppearance();
  // Two selected sets, not one lightened: the dark steps were chosen against a
  // dark surface.
  const dark =
    appearance.theme === "dark" ||
    (appearance.theme === "system" && document.documentElement.dataset.theme === "dark");
  const series = dark ? SERIES_DARK : SERIES_LIGHT;
  const others = dark ? OTHERS_DARK : OTHERS_LIGHT;

  const panels = useMemo(
    () => [
      { title: "By Name", unit: "holdings", slices: group(positions, (p) => p.symbol, series, others) },
      {
        title: "By Sector",
        unit: "sectors",
        slices: group(positions, (p) => p.sector ?? "Unclassified", series, others),
      },
      {
        title: LEVELS[level].label,
        unit: LEVELS[level].unit,
        slices: group(positions, (p) => p[level] ?? "Unclassified", series, others),
        level: (
          <select
            className="level-select"
            value={level}
            aria-label="Classification level"
            onChange={(e) => setLevel(e.target.value as Level)}
          >
            <option value="industry">Industry</option>
            <option value="basic_industry">Basic Industry</option>
          </select>
        ),
      },
    ],
    [positions, level, series, others],
  );

  // Worth saying once, under the panels: an Unclassified slice is missing
  // reference data, not a real category.
  const unclassified = positions.some((p) => !p.sector || !p[level]);

  if (positions.length === 0) return null;

  return (
    // No heading: the view is reached from a button that already names it.
    <>
      <div className="breakdown-grid">
        {panels.map((panel) => (
          <Panel
            key={panel.unit}
            title={panel.title}
            unit={panel.unit}
            slices={panel.slices}
            level={"level" in panel ? panel.level : undefined}
          />
        ))}
      </div>
      {unclassified && (
        <div className="hint" style={{ marginBottom: 16 }}>
          Some holdings have no sector or industry. Upload an NSE company list with an
          Industry column under Settings → Instrument Universe and they will group
          properly.
        </div>
      )}
    </>
  );
}

function Panel({
  title,
  unit,
  slices,
  level,
}: {
  title: string;
  unit: string;
  slices: Slice[];
  /** A control rendered in place of the heading, for the switchable panel. */
  level?: React.ReactNode;
}) {
  const [view, setView] = useState<View>("donut");
  const total = slices.reduce((a, s) => a + s.value, 0);

  return (
    <div className="card breakdown-panel">
      <div className="breakdown-head">
        {level ?? <span className="k">{title}</span>}
        <div className="view-toggle">
          {(["donut", "bar", "table"] as View[]).map((v) => (
            <button
              key={v}
              className={view === v ? "active" : ""}
              aria-label={`${title} as ${v}`}
              aria-pressed={view === v}
              title={`Show as ${v}`}
              onClick={() => setView(v)}
            >
              <ViewIcon view={v} />
            </button>
          ))}
        </div>
      </div>

      {view === "donut" && <Donut slices={slices} total={total} unit={unit} />}
      {view === "bar" && <Bars slices={slices} />}
      {view === "table" && <Table slices={slices} total={total} />}

      {/* Only the donut needs it — bars and the table name every row. */}
      {view === "donut" && (
        <div className="legend">
          {slices.map((s) => (
            <span key={s.key} className="legend-item" title={`${s.key}: ${rupee(s.value, 0)}`}>
              <i style={{ background: s.colour }} />
              {s.key} · {rupee(s.value, 0)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ViewIcon({ view }: { view: View }) {
  // 14px, currentColor, so the active state is a colour change and nothing else.
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      {view === "donut" && (
        <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="4" />
      )}
      {view === "bar" && (
        <g fill="currentColor">
          <rect x="1" y="2" width="12" height="2.6" rx="1" />
          <rect x="1" y="5.7" width="8" height="2.6" rx="1" />
          <rect x="1" y="9.4" width="5" height="2.6" rx="1" />
        </g>
      )}
      {view === "table" && (
        <g fill="currentColor">
          <rect x="1" y="2" width="12" height="2" rx="0.6" />
          <rect x="1" y="6" width="12" height="2" rx="0.6" />
          <rect x="1" y="10" width="12" height="2" rx="0.6" />
        </g>
      )}
    </svg>
  );
}

const SIZE = 180;
const R = 70;
const STROKE = 26;

function Donut({
  slices,
  total,
  unit,
}: {
  slices: Slice[];
  total: number;
  unit: string;
}) {
  const circumference = 2 * Math.PI * R;

  // Each arc starts where the previous one ended. Folded rather than tracked
  // in a counter, so nothing is reassigned while rendering.
  const arcs = slices.reduce<(Slice & { length: number; offset: number })[]>(
    (acc, slice) => {
      const previous = acc.at(-1);
      return [
        ...acc,
        {
          ...slice,
          length: (slice.pct / 100) * circumference,
          offset: previous ? previous.offset + previous.length : 0,
        },
      ];
    },
    [],
  );

  return (
    <svg
      className="donut"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={`Total ${rupee(total, 0)}`}
    >
      {arcs.map((s) => {
        // A 2px gap between fills, so touching slices stay distinct marks
        // rather than one continuous band.
        const drawn = Math.max(s.length - 2, 0);
        return (
          <circle
            key={s.key}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke={s.colour}
            strokeWidth={STROKE}
            strokeDasharray={`${drawn} ${circumference - drawn}`}
            strokeDashoffset={-s.offset}
            // Start at twelve o'clock rather than three.
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          >
            <title>{`${s.key}: ${rupee(s.value, 0)} (${s.pct.toFixed(1)}%)`}</title>
          </circle>
        );
      })}
      <text className="donut-total" x={SIZE / 2} y={SIZE / 2 - 4} textAnchor="middle">
        {rupee(total, 0)}
      </text>
      <text className="donut-sub" x={SIZE / 2} y={SIZE / 2 + 12} textAnchor="middle">
        {slices.length} {unit}
      </text>
    </svg>
  );
}

function Bars({ slices }: { slices: Slice[] }) {
  const max = Math.max(...slices.map((s) => s.value), 1);
  return (
    <div className="breakdown-bars">
      {slices.map((s) => (
        <div key={s.key} className="bar-row" title={`${s.key}: ${rupee(s.value, 0)}`}>
          <span className="bar-label">{s.key}</span>
          <span className="bar-track">
            <span style={{ width: `${(s.value / max) * 100}%`, background: s.colour }} />
          </span>
          <span className="bar-value">{rupee(s.value, 0)}</span>
        </div>
      ))}
    </div>
  );
}

function Table({ slices, total }: { slices: Slice[]; total: number }) {
  return (
    <table className="legs breakdown-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Value</th>
          <th>Share</th>
        </tr>
      </thead>
      <tbody>
        {slices.map((s) => (
          <tr key={s.key}>
            <td>
              <i className="legend-dot" style={{ background: s.colour }} />
              {s.key}
            </td>
            <td>{rupee(s.value, 0)}</td>
            <td>{s.pct.toFixed(1)}%</td>
          </tr>
        ))}
        <tr>
          <td>
            <strong>Total</strong>
          </td>
          <td>
            <strong>{rupee(total, 0)}</strong>
          </td>
          <td>100.0%</td>
        </tr>
      </tbody>
    </table>
  );
}
